<script lang="ts" setup>
import {
	reactive,
	ref,
	computed,
	nextTick,
	onMounted,
	onBeforeUnmount,
	useSlots,
} from 'vue'
import debounce from 'lodash-es/debounce'
import { useBreakpoints } from '@/lib/utils'

const props = defineProps({
	isShowDesktopSection: {
		type: Boolean,
		default: true,
	},
	isHideDivideBetweenTabs: {
		type: Boolean,
		default: false,
	},
})

const slots = useSlots()
const { isLarge: isDesktop } = useBreakpoints()
const isShow = reactive({
	arrowLeft: false,
	arrowRight: false,
	maskLeft: false,
	maskRight: false,
})

const tabScroll = ref<HTMLElement | null>(null)
const index = ref(0)
const tabsLength = ref(0)
const isTablet = ref(false)

const perPage = computed(() => {
	return isDesktop.value && props.isShowDesktopSection ? 5 : 3
})

const tabWidth = computed(() => {
	return isDesktop.value && props.isShowDesktopSection ? 136 : 160
})

const wrapWidthStyle = computed(() => {
	let width
	if (tabsLength.value < perPage.value) {
		width = tabsLength.value * tabWidth.value
	} else {
		width = perPage.value * tabWidth.value
	}
	return { width: isTablet.value ? `${width}px` : 'none' }
})

const position = computed(() => {
	return tabWidth.value * index.value
})

const scrollStyle = computed(() => {
	return {
		transform: isTablet.value ? `translateX(-${position.value}px)` : 'none',
	}
})

function resetTabs() {
	index.value = 0
	nextTick(() => {
		tabsLength.value = slots?.default?.length || 3
		controllArrowShowOrHide()
	})
}
function controllArrowShowOrHide() {
	const scrollElement = tabScroll.value
	if (!scrollElement) return
	isShow.maskLeft = scrollElement.scrollLeft !== 0
	if (isTablet.value) {
		isShow.maskRight = tabsLength.value - perPage.value > index.value
	} else {
		isShow.maskRight =
			scrollElement.scrollLeft + scrollElement.offsetWidth !==
			scrollElement.scrollWidth
	}
	isShow.arrowLeft = index.value !== 0
	isShow.arrowRight = index.value < tabsLength.value - perPage.value
}
function tabScrollTo(position: 'left' | 'right') {
	if (position === 'left') {
		if (index.value <= perPage.value) {
			index.value = 0
		} else {
			index.value -= perPage.value
		}
	}
	if (position === 'right') {
		if (
			tabsLength.value - perPage.value - (tabsLength.value % perPage.value) <=
			index.value
		) {
			index.value = tabsLength.value - perPage.value
		} else {
			index.value += perPage.value
		}
	}

	controllArrowShowOrHide()
}
function tabScrollToIndex(targetIndex: number) {
	const slidePageTimes = Math.floor((targetIndex - index.value) / perPage.value)
	const slideDirection = slidePageTimes > 0 ? 'right' : 'left'
	for (let i = 0; i < Math.abs(slidePageTimes); i += 1) {
		tabScrollTo(slideDirection)
	}
}

onMounted(() => {
	resetTabs()

	window.addEventListener('resize', resetTabs)
	window.addEventListener('resize', debounce(controllArrowShowOrHide, 100))
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', resetTabs)
	window.removeEventListener('resize', debounce(controllArrowShowOrHide, 100))
})
</script>
<template>
	<div
		:class="[
			'bs-tab-container',
			{
				'bs-tab-section': isShowDesktopSection,
				'bs-tab-container--hide-divide': isHideDivideBetweenTabs,
			},
		]"
	>
		<transition name="opacity">
			<div
				class="bs-tab-container-arrow-left"
				v-show="isShow.arrowLeft"
				@click="tabScrollTo('left')"
			>
				<i class="bs-icon">
					<span class="icon-tabs-arrow-left"></span>
				</i>
			</div>
		</transition>
		<div
			class="bs-tab-container-scroll-wrap"
			:class="{ 'bs-tab-container-scroll-wrap--hasFix': $slots.fix }"
			:style="wrapWidthStyle"
		>
			<div
				class="bs-tab-container-scroll-mask-left"
				v-show="isShow.maskLeft"
			></div>
			<div class="bs-tab-container-scroll-wrap" ref="tabScroll">
				<div class="bs-tab-container-scroll" :style="scrollStyle">
					<div class="bs-tab-container-scroll-inner">
						<slot name="default" />
					</div>
				</div>
			</div>
			<div
				class="bs-tab-container-scroll-mask-right"
				v-show="isShow.maskRight"
			></div>
		</div>
		<slot name="fix" class="bs-tab--fix" />
		<transition name="opacity">
			<div
				class="bs-tab-container-arrow-right"
				v-show="isShow.arrowRight"
				@click="tabScrollTo('right')"
			>
				<i class="bs-icon">
					<span class="icon-tabs-arrow-right"></span>
				</i>
			</div>
		</transition>
	</div>
</template>
